import {
  alpha,
  Box,
  Breadcrumbs,
  Link,
  Skeleton,
  Stack,
  styled,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import BackButton from '../../../../InstallFacilities/BackButton';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import _ from 'lodash';
import { NormalizedGroup } from '../../../../../../redux/structure/GroupsAdapter';
import SubHeaderEntityActions from './SubHeaderEntityActions/SubHeaderEntityActions';
import WorkspaceAPI from '../../../../../../redux/workspace/WorkspaceAPI';
import { skipToken } from '@reduxjs/toolkit/query';
import useIsAdmin from '../../../../WorkspaceAdmin/useIsAdmin';
import useViewLocationsPath from '../../../../../../shared/hooks/useViewLocationsPath';
interface SubHeaderLocationsProps {
  entity?: NormalizedGroup;
  marketId: string | undefined;
  pathGroups: NormalizedGroup[];
  loading?: boolean;
  type: string;
  subPages?: { name: string; url: string }[];
}
export function SubHeaderLocations(props: Readonly<SubHeaderLocationsProps>) {
  const {
    entity,
    type,
    pathGroups,
    loading: loadingProp,
    marketId,
    subPages,
  } = props;
  const { t } = useTranslation();
  const { data: workspace, isLoading: loadingWorkspace } =
    WorkspaceAPI.useGetWorkspaceByIdQuery(marketId ?? skipToken);
  const isAdmin = useIsAdmin();
  const { getViewLocationsPath } = useViewLocationsPath();
  const loading = loadingProp || loadingWorkspace;
  const goBackGroup = subPages?.length
    ? _.last(pathGroups)
    : _.nth(pathGroups, -2);
  let urlGoBack = '/markets';
  if (goBackGroup) {
    urlGoBack = getViewLocationsPath(goBackGroup.uuid);
  } else if (marketId || pathGroups.length === 1) {
    urlGoBack = getViewLocationsPath();
  }

  const overviewName = isAdmin
    ? t('view_locations.view_all')
    : t('device.menu.overview');
  const overviewUrl = isAdmin ? '/markets' : '/manager/view-locations';

  const listItems = [
    {
      name: overviewName,
      url: overviewUrl,
      key: 'overview',
    },
    ...(marketId
      ? [
          {
            name: workspace?.name,
            url: getViewLocationsPath(),
            key: `workspace:${workspace?.id}`,
          },
        ]
      : []),
    ...pathGroups.map((group) => ({
      name: group.name,
      url: getViewLocationsPath(group.uuid),
      key: `group:${group.uuid}`,
    })),
    ...(subPages?.map((subpage, i) => ({
      ...subpage,
      key: `subpage:${subpage.url}`,
    })) ?? []),
  ];
  const breadcrumbs = listItems.map((item, i) =>
    i === listItems.length - 1 ? (
      <Typography key={item.key} sx={{ color: 'text.primary' }}>
        {item.name}
      </Typography>
    ) : (
      <Link underline="hover" key={item.key} color="inherit" href={item.url}>
        {item.name}
      </Link>
    )
  );
  return (
    <StyledSubHeader>
      <Stack
        px={2}
        py={1}
        alignItems={'center'}
        spacing={4}
        direction={'row'}
        flex={'1 1 auto'}
      >
        <BackButton sx={{ width: '6rem' }} loading={loading} href={urlGoBack} />
        {loading ? (
          <Skeleton variant="text" width={200} />
        ) : (
          <Typography variant="body1" component={'div'}>
            <Breadcrumbs
              separator={<NavigateNextIcon fontSize="small" />}
              aria-label="breadcrumb"
            >
              {breadcrumbs}
            </Breadcrumbs>
          </Typography>
        )}
      </Stack>
      <Box>
        <SubHeaderEntityActions
          entity={entity}
          type={type}
          marketId={marketId}
        />
      </Box>
    </StyledSubHeader>
  );
}

const StyledSubHeader = styled(Box)(({ theme }) => ({
  backgroundColor: alpha(theme.palette.primary.main, 0.4),
  width: '100%',
  padding: theme.spacing(1),
  color: theme.palette.text.primary,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));
